import { visioReducer } from '@agent/core/basket/visio/visio-basket.store';
import { EntityState, toUpdate } from '@commons/util/utilities/store/entity';

import { Action, ChannelType, combineReducers, Reducer, StoreService, toString } from '@contact/upgrade';

import { ActionsUnion, ActionTypes } from './basket.action';
import {
  ADAPTER_BASKET_CATEGORY,
  ADAPTER_BASKET_CHANNELS,
  ADAPTER_BASKET_LABEL,
  ADAPTER_BASKET_REQUEST_IDS,
  ADAPTER_BASKET_REQUESTS,
  BasketCategoryModel,
  BasketChannelModel,
  BasketId,
  BasketLabelModel,
  BasketModel,
  BasketStoreModel,
  INITIAL_STATE,
} from './basket.model';
import { mailReducer } from './mail/mail-basket.store';

const MODULE_NAME = 'basketStore';

/**
 * Main reducer to manage basket store
 */
export function basketReducer(state: BasketStoreModel, action: ActionsUnion): BasketStoreModel {
  switch (action.type) {
    case ActionTypes.OPEN_BASKET:
      return {
        ...state,
        open: true,
      };
    case ActionTypes.CLOSE_BASKET:
      return {
        ...state,
        open: false,
      };
    case ActionTypes.ADD_CHANNEL: {
      const { channel } = action;
      return {
        ...state,
        channels: ADAPTER_BASKET_CHANNELS.addOne(channel, state.channels),
      };
    }
    case ActionTypes.DELETE_CHANNEL: {
      const { channelType } = action;
      return {
        ...state,
        channels: ADAPTER_BASKET_CHANNELS.removeOne(toString(channelType), state.channels),
      };
    }
    case ActionTypes.CHANGE_CHANNEL: {
      const { channelType } = action;
      return {
        ...state,
        channelSelected: channelType,
      };
    }
    // action for channel given by action
    case ActionTypes.SUCCESS_LOAD:
    case ActionTypes.FAILURE_LOAD:
    case ActionTypes.SET_PAGE_SIZE: {
      // use channel given by action
      const { channel } = action;
      // if no channel state
      if (!state.channels.entities[channel]) return state;
      return {
        ...state,
        channels: ADAPTER_BASKET_CHANNELS.updateOne(
          {
            id: channel,
            changes: channelReducer(state.channels.entities[channel], action),
          },
          state.channels
        ),
      };
    }
    // action for current selected channel
    case ActionTypes.REFRESH:
    case ActionTypes.CHANGE_CATEGORY:
    case ActionTypes.CHANGE_LABEL:
    case ActionTypes.CHANGE_PAGE:
    case ActionTypes.OPEN_REQUEST:
    case ActionTypes.SUCCESS_OPEN_REQUEST:
    case ActionTypes.FAILURE_OPEN_REQUEST:
    case ActionTypes.UPDATE_REQUEST:
    case ActionTypes.SELECT_ALL:
    case ActionTypes.UNSELECT_ALL:
    case ActionTypes.SELECT:
    case ActionTypes.UNSELECT:
    case ActionTypes.SET_REQUEST_ERROR:
    case ActionTypes.RESET_REQUEST_ERROR:
    case ActionTypes.RESET_CACHE: {
      // use selected channel from store
      const { channelSelected } = state;
      // if no channel state
      if (!state.channels.entities[channelSelected]) return state;
      return {
        ...state,
        channels: ADAPTER_BASKET_CHANNELS.updateOne(
          {
            id: channelSelected,
            changes: channelReducer(state.channels.entities[channelSelected], action),
          },
          state.channels
        ),
      };
    }
    default:
      return state;
  }
}

/**
 * Meta reducer for channel reducer behavior
 * Share with all channel reducer
 */
function channelReducer(state: BasketChannelModel, action: ActionsUnion): BasketChannelModel {
  switch (action.type) {
    case ActionTypes.SET_PAGE_SIZE: {
      const { pageSize } = action;
      return {
        ...state,
        pageSize,
      };
    }
    case ActionTypes.CHANGE_CATEGORY: {
      const { category } = action;
      // if no label state
      if (!state.categories.entities[category]) return state;
      // retrieve last label selected
      const { lastLabelSelected } = state.categories.entities[category];
      // compute new state
      return {
        ...state,
        categorySelected: category,
        labelSelected: lastLabelSelected,
      };
    }
    case ActionTypes.CHANGE_LABEL: {
      const { label } = action;
      // if no label state
      if (!state.labels.entities[label]) return state;
      // category state
      const { category } = state.labels.entities[label];
      // compute new state
      return {
        ...state,
        categorySelected: category,
        // save last label selected from category
        categories: ADAPTER_BASKET_CATEGORY.updateOne(toUpdate(category, { lastLabelSelected: label }), state.categories),
        labelSelected: label,
      };
    }
    // action generic whose don't use current state
    case ActionTypes.SUCCESS_LOAD:
    case ActionTypes.FAILURE_LOAD:
    case ActionTypes.OPEN_REQUEST:
    case ActionTypes.FAILURE_OPEN_REQUEST:
    case ActionTypes.SUCCESS_OPEN_REQUEST:
    case ActionTypes.RESET_CACHE:
      return {
        ...state,
        loading: loadingReducer(state.loading, action),
        labels: labelListReducer(state.labels, action),
      };
    // ation for current label selected
    case ActionTypes.REFRESH:
    case ActionTypes.CHANGE_PAGE:
    case ActionTypes.SELECT_ALL:
    case ActionTypes.UNSELECT_ALL:
    case ActionTypes.SELECT:
    case ActionTypes.UPDATE_REQUEST:
    case ActionTypes.UNSELECT:
    case ActionTypes.SET_REQUEST_ERROR:
    case ActionTypes.RESET_REQUEST_ERROR: {
      // use label from the store
      const { categorySelected, labelSelected } = state;
      // if no label or category state
      if (!state.labels.entities[labelSelected] || !state.categories.entities[categorySelected]) {
        return state;
      }
      return {
        ...state,
        loading: loadingReducer(state.loading, action),
        categories: ADAPTER_BASKET_CATEGORY.updateOne(
          {
            id: categorySelected,
            changes: categoryReducer(state.categories.entities[categorySelected], action),
          },
          state.categories
        ),
        labels: ADAPTER_BASKET_LABEL.updateOne(
          {
            id: labelSelected,
            changes: labelReducer(state.labels.entities[labelSelected], action),
          },
          state.labels
        ),
      };
    }
    default:
      return state;
  }
}

/**
 * Sub reducer to manage loading
 */
function loadingReducer(state: boolean, action: ActionsUnion): boolean {
  switch (action.type) {
    case ActionTypes.REFRESH:
    case ActionTypes.CHANGE_PAGE:
    case ActionTypes.OPEN_REQUEST:
      return true;
    case ActionTypes.SUCCESS_LOAD:
    case ActionTypes.FAILURE_LOAD:
    case ActionTypes.SUCCESS_OPEN_REQUEST:
    case ActionTypes.FAILURE_OPEN_REQUEST:
      return false;
    default:
      return state;
  }
}

/**
 * Sub reducer to manage category
 */
function categoryReducer(state: BasketCategoryModel, action: ActionsUnion): BasketCategoryModel {
  switch (action.type) {
    case ActionTypes.REFRESH: {
      const { sortKey } = action;
      const { defaultKey, key, order } = state.sort;
      if (!sortKey) {
        return state;
      }
      return {
        ...state,
        sort: {
          ...state.sort,
          key: sortKey || defaultKey,
          // inverse order if same key and order is not given
          order: key === sortKey ? (order === 'desc' ? 'asc' : 'desc') : 'asc',
        },
      };
    }
    default:
      return state;
  }
}

/**
 * Sub reducer to manage label list
 */
function labelListReducer(state: EntityState<BasketLabelModel>, action: ActionsUnion): EntityState<BasketLabelModel> {
  switch (action.type) {
    case ActionTypes.SUCCESS_LOAD:
    case ActionTypes.FAILURE_LOAD: {
      const { label } = action;
      return ADAPTER_BASKET_LABEL.updateOne(
        {
          id: label,
          changes: labelReducer(state.entities[label], action),
        },
        state
      );
    }
    case ActionTypes.RESET_CACHE: {
      // reset timestamp for all label
      const updates = state.ids.map(id => toUpdate(id, { timestamp: 0 }));
      return ADAPTER_BASKET_LABEL.updateMany(updates, state);
    }
    default:
      return state;
  }
}

/**
 * Sub reducer to manage label
 */
function labelReducer(state: BasketLabelModel, action: ActionsUnion): BasketLabelModel {
  switch (action.type) {
    case ActionTypes.REFRESH:
      return {
        ...state,
        currentPage: action?.refreshType === 'auto' ? state.currentPage : 1,
        requests: requestsReducer(state.requests, action),
        requestsInError: requestsInErrorReducer(state.requestsInError, action),
      };
    case ActionTypes.CHANGE_PAGE: {
      const { page } = action;
      return {
        ...state,
        currentPage: page,
      };
    }
    case ActionTypes.SUCCESS_LOAD: {
      const { filtered, nbRequests, requests } = action.result;
      const { requestsInError, requestsSelected } = state;
      if (!requests) {
        // if action doesn't contains request
        return {
          ...state,
          nbTotalRequests: nbRequests === undefined ? state.nbTotalRequests : nbRequests,
        };
      }
      // auto compute model field
      let overrideRequests = requests.map(r => ({
        ...r,
        error: requestsInError.some(id => toString(id) === r.id),
        selected: requestsSelected.some(id => toString(id) === r.id),
      }));
      return {
        ...state,
        timestamp: new Date().getTime(),
        nbTotalRequests: !filtered ? nbRequests : state.nbTotalRequests,
        nbRequests,
        requests: requestsReducer(state.requests, {
          ...action,
          result: {
            ...action.result,
            requests: overrideRequests,
          },
        }),
      };
    }
    case ActionTypes.SELECT_ALL:
    case ActionTypes.UNSELECT_ALL: {
      let overrideAction = {
        ...action,
        ids: action.ids || state.requests.ids,
      };
      return {
        ...state,
        requests: requestsReducer(state.requests, overrideAction),
        requestsInError: requestsInErrorReducer(state.requestsInError, overrideAction),
        requestsSelected: requestsSelectedReducer(state.requestsSelected, overrideAction),
      };
    }
    case ActionTypes.SELECT:
    case ActionTypes.UNSELECT:
    case ActionTypes.SET_REQUEST_ERROR:
    case ActionTypes.RESET_REQUEST_ERROR:
    case ActionTypes.UPDATE_REQUEST:
      return {
        ...state,
        requests: requestsReducer(state.requests, action),
        requestsInError: requestsInErrorReducer(state.requestsInError, action),
        requestsSelected: requestsSelectedReducer(state.requestsSelected, action),
      };
    default:
      return state;
  }
}

/**
 * Sub reducer to manage request list
 */
function requestsReducer(state: EntityState<BasketModel>, action: ActionsUnion): EntityState<BasketModel> {
  switch (action.type) {
    // replace all request list with given requests
    case ActionTypes.SUCCESS_LOAD: {
      const { requests } = action.result;
      if (!requests) return state;
      return ADAPTER_BASKET_REQUESTS.addMany(requests, ADAPTER_BASKET_REQUESTS.getInitialState());
    }
    // set to true all selected value
    case ActionTypes.SELECT_ALL: {
      const { ids } = action;
      return ADAPTER_BASKET_REQUESTS.updateMany(
        (ids || []).map(id => toUpdate(id, { selected: true })),
        state
      );
    }
    // set to false all selected value
    case ActionTypes.UNSELECT_ALL: {
      const { ids } = action;
      return ADAPTER_BASKET_REQUESTS.updateMany(
        (ids || []).map(id => toUpdate(id, { selected: false })),
        state
      );
    }
    // set to true selected value for given id request
    case ActionTypes.SELECT: {
      const { id } = action;
      return ADAPTER_BASKET_REQUESTS.updateOne(toUpdate(id, { selected: true }), state);
    }
    // set to false selected value for given id request
    case ActionTypes.UNSELECT: {
      const { id } = action;
      return ADAPTER_BASKET_REQUESTS.updateOne(toUpdate(id, { selected: false }), state);
    }
    // set to true error value for given id request
    case ActionTypes.SET_REQUEST_ERROR: {
      const { ids } = action;
      return ADAPTER_BASKET_REQUESTS.updateMany(
        ids.map(id => toUpdate(id, { error: true })),
        state
      );
    }
    // set to false all error value
    case ActionTypes.RESET_REQUEST_ERROR: {
      const { ids } = state;
      return ADAPTER_BASKET_REQUESTS.updateMany(
        ids.map(id => toUpdate(id, { error: false })),
        state
      );
    }
    // update provided request key value
    case ActionTypes.UPDATE_REQUEST: {
      const { requestId, change } = action;
      return ADAPTER_BASKET_REQUESTS.updateOne(toUpdate(requestId, change), state);
    }
    default:
      return state;
  }
}

/**
 * Sub reducer to manage request in error list
 */
function requestsInErrorReducer(state: BasketId[], action: ActionsUnion): BasketId[] {
  switch (action.type) {
    case ActionTypes.SET_REQUEST_ERROR: {
      const { ids } = action;
      return ADAPTER_BASKET_REQUEST_IDS.addMany(ids, state);
    }
    case ActionTypes.REFRESH:
    case ActionTypes.RESET_REQUEST_ERROR:
      return [];
    default:
      return state;
  }
}

/**
 * Sub reducer to manage request selected list
 */
function requestsSelectedReducer(state: BasketId[], action: ActionsUnion): BasketId[] {
  switch (action.type) {
    case ActionTypes.SELECT_ALL: {
      const { ids } = action;
      return ADAPTER_BASKET_REQUEST_IDS.addMany(ids || [], state);
    }
    case ActionTypes.UNSELECT_ALL: {
      const { ids } = action;
      return ADAPTER_BASKET_REQUEST_IDS.removeMany(
        (ids || []).map(id => toString(id)),
        state
      );
    }
    case ActionTypes.SELECT: {
      const { id } = action;
      return ADAPTER_BASKET_REQUEST_IDS.addOne(id, state);
    }
    case ActionTypes.UNSELECT: {
      const { id } = action;
      return ADAPTER_BASKET_REQUEST_IDS.removeOne(toString(id), state);
    }
    default:
      return state;
  }
}

/**
 * Convert specific reducer channel to use with basket store
 */
export function toChannelReducer<T extends BasketChannelModel>(channel: ChannelType, reducer: Reducer<T>): Reducer<BasketStoreModel> {
  return (state: BasketStoreModel, action: Action): BasketStoreModel => {
    const { channelSelected } = state;
    const channelState = state.channels.entities[channel];
    // if reducer should be call
    if (channelSelected !== channel || !channelState) return state;
    return {
      ...state,
      channels: ADAPTER_BASKET_CHANNELS.updateOne(
        {
          id: channel,
          changes: reducer(channelState as T, action),
        },
        state.channels
      ),
    };
  };
}

/**
 * Declare store like angular service
 * Don't implements method here
 * Store must be used just for reducer mechanism
 * Prefer normal service to implements method
 */
export interface IBasketStore extends StoreService<BasketStoreModel> {}

export class BasketStore extends StoreService<BasketStoreModel> implements IBasketStore {
  static override $inject: Array<string> = [];

  constructor() {
    // Store should be a pure function, use action to manipulate state // Don't inject service here
    super(combineReducers(basketReducer, mailReducer, visioReducer), INITIAL_STATE);
  }
}

export default MODULE_NAME;
